import React, { useEffect, useState, useCallback } from "react";
import { Trophy, TrendingUp, TrendingDown, Activity, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const fmtMoney = (n) => "$" + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct = (n) => (n >= 0 ? "+" : "") + (n || 0).toFixed(2) + "%";

const STRAT_COLORS = {
  MAG7: "#a855f7",
  QQQ_HOLD: "#3b82f6",
  SPY_HOLD: "#22c55e",
  VTI_HOLD: "#10b981",
  MOM_TOP5: "#f59e0b",
  MOM_TOP10: "#fb923c",
  LOW_VOL_20: "#06b6d4",
  ALL_WEATHER: "#94a3b8",
  NEXT7: "#ec4899",
  CRYPTO_5050: "#f97316",
  RAW_CASH: "#6b7280",
};

function MiniSparkline({ data, color, width = 100, height = 30 }) {
  if (!data || data.length < 2) return <div style={{ width, height, color: "#666", fontSize: 9 }}>—</div>;
  const navs = data.map(d => d.nav);
  const min = Math.min(...navs);
  const max = Math.max(...navs);
  const range = max - min || 1;
  const path = data.map((d, i) =>
    `${i === 0 ? "M" : "L"} ${(i / (data.length - 1)) * width} ${height - ((d.nav - min) / range) * height}`
  ).join(" ");
  return (
    <svg width={width} height={height}>
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" />
    </svg>
  );
}

export default function ArenaTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) { setError("Not logged in"); setLoading(false); return; }
    try {
      const r = await fetch(`${API}/api/skynet-brain/arena`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 401) { setError("Session expired"); setLoading(false); return; }
      const j = await r.json();
      if (j.error) setError(j.error);
      else { setData(j); setError(null); }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 60000);
    return () => clearInterval(iv);
  }, [fetchData]);

  if (error && !data) return <div style={{ padding: 40, color: "#ef4444", textAlign: "center" }}>{error}</div>;
  if (loading && !data) return <div style={{ padding: 40, color: "#888", textAlign: "center" }}>Loading arena…</div>;
  if (!data) return null;

  const lb = data.leaderboard || [];
  const totalCapital = lb.reduce((s, p) => s + (p.initial_capital || 0), 0);
  const totalNav = lb.reduce((s, p) => s + (p.nav || 0), 0);
  const totalPnl = totalNav - totalCapital;
  const winners = lb.filter(p => p.pnl_pct > 0).length;
  const losers = lb.filter(p => p.pnl_pct < 0).length;

  const winner = lb[0];

  return (
    <div style={{ padding: "16px 20px", color: "#eee", maxWidth: 1600, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0f1218, #1a1d29)", border: "1px solid #2a2d39", borderRadius: 16, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <Trophy size={24} style={{ color: "#facc15" }} />
          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>Strategy Arena</div>
          <span style={{ fontSize: 11, padding: "3px 8px", background: "#facc1533", color: "#facc15", borderRadius: 4, fontWeight: 600 }}>
            11 PORTFOLIOS · ${(totalCapital/1000)}K NOTIONAL
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: "#888" }}>TOTAL CAPITAL</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>{fmtMoney(totalCapital)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888" }}>TOTAL NAV</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: totalPnl >= 0 ? "#22c55e" : "#ef4444" }}>{fmtMoney(totalNav)}</div>
            <div style={{ fontSize: 11, color: totalPnl >= 0 ? "#22c55e" : "#ef4444" }}>
              {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888" }}>WINNERS / LOSERS</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              <span style={{ color: "#22c55e" }}>{winners}</span> / <span style={{ color: "#ef4444" }}>{losers}</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888" }}>🥇 LEADER</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: STRAT_COLORS[winner?.id] || "#fff" }}>{winner?.name}</div>
            <div style={{ fontSize: 11, color: winner?.pnl_pct >= 0 ? "#22c55e" : "#ef4444" }}>{fmtPct(winner?.pnl_pct)}</div>
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div style={{ background: "#13161f", border: "1px solid #2a2d39", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", fontSize: 14, fontWeight: 600, color: "#fff", borderBottom: "1px solid #2a2d39", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>📊 Live Leaderboard</span>
          <button onClick={fetchData} style={{ background: "transparent", border: "none", color: "#888", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#0a0c14", color: "#666", textTransform: "uppercase", fontSize: 10 }}>
              <th style={{ padding: 10, textAlign: "left" }}>#</th>
              <th style={{ padding: 10, textAlign: "left" }}>Strategy</th>
              <th style={{ padding: 10, textAlign: "left" }}>Description</th>
              <th style={{ padding: 10, textAlign: "right" }}>NAV</th>
              <th style={{ padding: 10, textAlign: "right" }}>P&L</th>
              <th style={{ padding: 10, textAlign: "right" }}>P&L %</th>
              <th style={{ padding: 10, textAlign: "center" }}>Holdings</th>
              <th style={{ padding: 10, textAlign: "center" }}>Rebalance</th>
              <th style={{ padding: 10, textAlign: "center" }}>Trend</th>
            </tr>
          </thead>
          <tbody>
            {lb.map((p, i) => {
              const isWin = p.pnl_pct >= 0;
              const color = STRAT_COLORS[p.id] || "#888";
              const rank = i + 1;
              const rankEmoji = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : "";
              const hist = data.history?.[p.id] || [];
              return (
                <tr key={p.id} style={{ borderTop: "1px solid #1a1d29" }}>
                  <td style={{ padding: 10, fontWeight: 700, color: "#fff" }}>
                    {rankEmoji} {rank}
                  </td>
                  <td style={{ padding: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{p.name}</div>
                    <div style={{ fontSize: 9, color, fontWeight: 600 }}>{p.id}</div>
                  </td>
                  <td style={{ padding: 10, color: "#888", fontSize: 11, maxWidth: 200 }}>{p.description}</td>
                  <td style={{ padding: 10, color: "#fff", textAlign: "right", fontWeight: 600 }}>{fmtMoney(p.nav)}</td>
                  <td style={{ padding: 10, color: isWin ? "#22c55e" : "#ef4444", textAlign: "right" }}>
                    {p.pnl >= 0 ? "+" : ""}${p.pnl?.toFixed(2)}
                  </td>
                  <td style={{ padding: 10, color: isWin ? "#22c55e" : "#ef4444", textAlign: "right", fontWeight: 700 }}>
                    {fmtPct(p.pnl_pct)}
                  </td>
                  <td style={{ padding: 10, textAlign: "center", color: "#aaa" }}>{p.holdings_count}</td>
                  <td style={{ padding: 10, textAlign: "center", color: "#666", fontSize: 10 }}>{p.rebalance}</td>
                  <td style={{ padding: 10, textAlign: "center" }}>
                    <MiniSparkline data={hist} color={color} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 20, padding: 12, fontSize: 10, color: "#555", textAlign: "center" }}>
        Auto-refresh 60s • Daily snapshots 16:00 + 21:00 UTC weekdays + weekend crypto • Each portfolio $10k paper
      </div>
    </div>
  );
}
